import moment from 'moment';
import { authUtilizationFilterStatusEnums, authUtilizationPeriodEnums, authUtilizationStatusEnums } from 'utils';

export const AUTH_UTILIZATION_ACTION = 'GET_AUTH_UTILIZATION';

/** Clients per request; the next page loads when the bottom of the list scrolls into view. */
export const PAGE_SIZE = 20;
/** Placeholder rows in the first-load skeleton. */
export const SKELETON_ROWS = 8;

/** Lower edge of the on-target band — mirrors the API's AUTH_UTILIZATION_ON_TARGET_MIN; used only for the "behind pace" note. */
export const ON_TARGET_MIN = 85;
/** Days a term runs before it gets a pace status or projection — mirrors the API's AUTH_UTILIZATION_TERM_PACE_MIN_DAYS. */
export const TERM_PACE_MIN_DAYS = 7;

/**
 * Visible range: 5 weeks of history before the anchor date, 11 weeks ahead. Arrows move the anchor a month.
 * Weeks start on Monday (`isoWeek`) — the same weeks the API buckets units into and the booking check limits.
 */
export const WEEKS_BEFORE = 5;
export const WEEKS_AFTER = 11;

export const getTimelineRange = (anchor) => {
    const start = moment(anchor).startOf('isoWeek').subtract(WEEKS_BEFORE, 'weeks');
    const end = moment(anchor).startOf('isoWeek').add(WEEKS_AFTER, 'weeks').subtract(1, 'day');
    return { start, end, totalDays: end.diff(start, 'days') + 1 };
};

/** Position of a date inside the range, as a 0–100 percentage. `endOfDay` places it on the day's right edge. */
export const dateToPercent = (date, range, endOfDay = false) => {
    const days = moment(date).startOf('day').diff(range.start, 'days') + (endOfDay ? 1 : 0);
    return Math.min(100, Math.max(0, (days / range.totalDays) * 100));
};

export const buildMonths = (range) => {
    const months = [];
    let cursor = range.start.clone().startOf('month');
    while (!cursor.isAfter(range.end)) {
        const from = moment.max(cursor.clone(), range.start);
        const to = moment.min(cursor.clone().endOf('month').startOf('day'), range.end);
        months.push({
            key: cursor.format('YYYY-MM'),
            label: cursor.format('MMMM'),
            date: cursor.clone(),
            left: dateToPercent(from, range),
            width: dateToPercent(to, range, true) - dateToPercent(from, range),
        });
        cursor = cursor.add(1, 'month');
    }
    return months;
};

export const buildWeekTicks = (range) => {
    const ticks = [];
    const cursor = range.start.clone();
    while (!cursor.isAfter(range.end)) {
        ticks.push({ key: cursor.format('YYYY-MM-DD'), label: cursor.format('D'), left: dateToPercent(cursor, range) });
        cursor.add(1, 'week');
    }
    return ticks;
};

export const STATUS_THEME = {
    [authUtilizationStatusEnums.ON_TARGET]: {
        label: 'On target',
        dot: '#038A4D',
        text: '#026C3C',
        border: '#A7D8B9',
        bg: '#E9F7EE',
        bar: '#16A34A',
        track: '#CFEDDB',
        solid: '#A7D8B9',
        light: '#DCFAE6',
    },
    [authUtilizationStatusEnums.OVER]: {
        label: 'Overutilized',
        dot: '#F04438',
        text: '#D92D20',
        border: '#FDA29B',
        bg: '#FEF3F2',
        bar: '#D92D20',
        track: '#FEE4E2',
        solid: '#D92D20',
        light: '#FEE4E2',
    },
    [authUtilizationStatusEnums.UNDER]: {
        label: 'Underutilized',
        dot: '#EF6820',
        text: '#B93815',
        border: '#F9DBAF',
        bg: '#FEF6EE',
        bar: '#E04F16',
        track: '#FDEAD7',
        solid: '#DC6803',
        light: '#FDEAD7', // orange-100, same as the chip track — was yellow, which read as a different status
    },
};

export const AVAILABLE_THEME = { label: 'Available', dot: '#D0D5DD', text: '#98A2B3', border: '#EAECF0', bg: '#F9FAFB' };

/** Selected-state tint of the Available / Scheduled / Cancelled status chips (the pace chips use STATUS_THEME). */
export const NEUTRAL_CHIP_THEME = { dot: '#667085', border: '#D0D5DD', bg: '#F2F4F7' };

/**
 * Status filter dot per option id (`authUtilizationStatuses`), copied from the legend: the three pace dots,
 * the Available dot, and the Scheduled / Cancelled-expired swatches (`.auth-util-legend-scheduled` / `-inactive`).
 * A function, not a module-level map: `utils` and `store` import each other, so enums can be undefined at load.
 */
export const filterStatusDot = (id) => {
    if (STATUS_THEME[id]) return { background: STATUS_THEME[id].dot };
    if (id === authUtilizationFilterStatusEnums.AVAILABLE) return { background: AVAILABLE_THEME.dot };
    if (id === authUtilizationFilterStatusEnums.SCHEDULED) return { background: '#F2F4F7', border: '1px solid #D0D5DD', boxSizing: 'border-box' };
    if (id === authUtilizationFilterStatusEnums.CANCELLED) {
        return { background: 'repeating-linear-gradient(-45deg, #F2F4F7 0 2px, #D0D5DD 2px 3px)' };
    }
    return {};
};

/** Units can be fractional (partial units from appt duration) — show at most 2 decimals, no trailing zeros. */
export const formatUnits = (units) => Number((Number(units) || 0).toFixed(2)).toString();

export const renderUnits = (auth) => {
    if (auth?.period === authUtilizationPeriodEnums.TERM) return 'entire term';
    const suffix = auth?.period === authUtilizationPeriodEnums.MONTH ? 'mo' : 'wk';
    return `${formatUnits(auth?.unitsPerPeriod)}u/${suffix}`;
};

export const renderClientShortName = (client) => `${client?.firstName || ''} ${client?.lastName ? `${client.lastName[0]}.` : ''}`.trim();
